import { useEffect, useRef } from 'react';
import posthog from 'posthog-js';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, LogOut, CreditCard, MessageCircle, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/components/AuthProvider';
import { useManualPaywall } from '@/hooks/useManualPaywall';
import { useCrisp } from '@/hooks/useCrisp';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { useLicenseExempt } from '@/hooks/useLicenseExempt';
import { isBillingTabVisible } from '@/components/billing/billingActions';

const SUPPORT_EMAIL = 'benoit.raillard@hector.legal';

/**
 * Full-screen blocking overlay triggered when
 * `cabinet_members.feature_flags.force_paywall === true` (set from the
 * Super Admin drawer → "Paywall forcé" toggle).
 *
 * Resilience guarantees:
 *  - Renders nothing when flag is not strictly `true`.
 *  - Renders nothing during loading or on fetch errors (fail-open).
 *  - Sits at z-[9999] so it cannot be bypassed by other modals.
 */
const ManualPaywallOverlay = () => {
  const { isPaywallActive, isLoading } = useManualPaywall();
  const { signOut, user } = useAuth();
  const { openChat } = useCrisp();
  const navigate = useNavigate();
  // Un membre exempté de licence qui ne gère aucun abonnement n'a pas d'onglet
  // Facturation : lui proposer « Procéder au paiement » mènerait à l'onglet Profil.
  const { isLicenseExempt } = useLicenseExempt();
  const { data: billingStatus } = useBillingStatus();
  const canPayFromSettings = isBillingTabVisible(isLicenseExempt, billingStatus);

  // Audit trail — fire once per session activation so we have a PostHog signal
  // if a user is wrongly blocked (allows quick rollback by clearing the flag).
  // Also a console.warn for live debugging when a user calls support.
  const announcedRef = useRef(false);
  useEffect(() => {
    if (isPaywallActive && !announcedRef.current) {
      announcedRef.current = true;
      try {
        posthog.capture('manual_paywall_shown', {
          user_id: user?.id ?? null,
          email: user?.email ?? null,
        });
      } catch {
        /* noop — telemetry must never break the overlay */
      }
      // eslint-disable-next-line no-console
      console.warn('[ManualPaywall] activated for user', {
        userId: user?.id,
        email: user?.email,
      });
    }
  }, [isPaywallActive, user?.id, user?.email]);

  if (isLoading || !isPaywallActive) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex overflow-y-auto p-4 bg-charcoal/80 backdrop-blur-md"
      role="alertdialog"
      aria-modal="true"
      aria-label="Accès suspendu : paiement en attente"
    >
      {/* `m-auto` dans un conteneur qui défile, plutôt que `items-center` : centré
          quand la carte tient, défilable quand elle dépasse la hauteur d'un
          téléphone, sans quoi les boutons de paiement sortaient de l'écran. */}
      <div className="m-auto w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-grey-200 sm:p-8">
        {/* Icon */}
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-grey-100">
          <ShieldAlert className="h-8 w-8 text-charcoal" />
        </div>

        {/* Heading */}
        <h2 className="text-center font-inter text-xl font-semibold text-grey-900">
          Accès suspendu : paiement en attente
        </h2>

        {/* Body */}
        <div className="mt-3 space-y-3 font-inter text-sm leading-relaxed text-grey-600">
          <p>
            Nous avons activé manuellement la suspension de votre accès car,
            malgré plusieurs relances, nous n'avons pas reçu le paiement de
            votre abonnement.
          </p>
          <p>
            Votre compte reste bloqué tant que le règlement n'a pas été
            réceptionné. Pour le régulariser, vous pouvez procéder au paiement
            ci-dessous.
          </p>
          <p className="text-grey-700">
            Une fois le paiement effectué, prévenez-nous (chat ou email) pour
            que nous retirions rapidement la restriction.
          </p>
        </div>

        {/* Actions */}
        <div className="mt-8 flex flex-col gap-3">
          {/* Le lien de paiement Stripe statique (buy.stripe.com) est retiré :
              le règlement passe désormais par Settings → Facturation, qui
              porte le vrai flux Checkout du cabinet (P4, 2026-08-12). */}
          {canPayFromSettings && (
            <Button
              onClick={() => navigate('/settings#facturation')}
              className="w-full bg-charcoal hover:bg-charcoal-medium"
            >
              <CreditCard className="mr-2 h-4 w-4" />
              Procéder au paiement
            </Button>
          )}

          <Button
            onClick={() => openChat()}
            variant="outline"
            className="w-full border-grey-200 text-grey-700 hover:bg-grey-50 hover:text-charcoal"
          >
            <MessageCircle className="mr-2 h-4 w-4" />
            Prévenir le support (chat)
          </Button>

          <Button
            onClick={() =>
              window.open(
                `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
                  'Paiement effectué : demande de levée du blocage',
                )}`,
                '_blank',
                'noopener,noreferrer',
              )
            }
            variant="outline"
            className="w-full border-grey-200 text-grey-700 hover:bg-grey-50 hover:text-charcoal"
          >
            <Mail className="mr-2 h-4 w-4" />
            Prévenir le support (email)
          </Button>

          <Button
            onClick={() => signOut()}
            variant="ghost"
            className="w-full text-grey-500 hover:bg-grey-50 hover:text-charcoal"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Se déconnecter
          </Button>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-grey-400">
          {SUPPORT_EMAIL} · Hector AI
        </p>
      </div>
    </div>
  );
};

export default ManualPaywallOverlay;
